import React, { useCallback } from 'react';

import { Button } from '@lightspeed/flame/Button';
import { Box } from '@lightspeed/flame/Core';
import { ModalBody, ModalHeader } from '@lightspeed/flame/Modal';
import { Tab, TabContainer } from '@lightspeed/flame/Tab';
import { Text, TextLink } from '@lightspeed/flame/Text';
import { Tooltip } from '@lightspeed/flame/Tooltip';
import { useQuery } from '@tanstack/react-query';

import { createReactRenderer } from '../helpers/react-renderer';

import { GitHubCommitApiResponse } from './common.types';
import { StyledImage, StyledModal } from './Components.styles';
import DiffList, { CommitInfoRow } from './DiffList';

enum RELEASE_COMMIT_LOG_VIEW {
  INCLUDED = 'INCLUDED',
  NOT_INCLUDED = 'NOT_INCLUDED',
}

export const TEN_MIN_STALE_TIME = 1000 * 60 * 10;

export const BOLD_FONT = {
  fontWeight: 'bold',
} as any;

export const dateFormatOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour12: true,
  hour: '2-digit',
  minute: '2-digit',
} as Intl.DateTimeFormatOptions;

const DeploymentInfoModal = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [deployedHash, setDeployedHash] = React.useState<string | null>(null);
  const [commitLogView, setCommitLogView] = React.useState(RELEASE_COMMIT_LOG_VIEW.INCLUDED);

  React.useEffect(() => {
    const commitHash =
      document
        .querySelector("link[rel='preload']")
        ?.getAttribute('href')
        ?.split('lshash-')?.[1]
        ?.split('/')?.[0] ?? null;
    setDeployedHash(commitHash);
  }, []);

  const getRecentCommits = useCallback(async () => {
    const data = deployedHash
      ? ((await fetch(
          `/staff/deployment-info/commits?&sha=${deployedHash}&page=1&per_page=10`
        ).then((res) => (res.ok ? res.json() : []))) as GitHubCommitApiResponse[])
      : [];
    return data;
  }, [deployedHash]);

  const {
    data: commitData,
    error: commitFetchError,
    refetch: refetchCommits,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [`githubCommit-${deployedHash}`],
    queryFn: getRecentCommits,
    staleTime: TEN_MIN_STALE_TIME,
    enabled: !!deployedHash,
  });

  if (commitFetchError) return null;

  return (
    <>
      {commitData?.length ? (
        <>
          <Tooltip content="See deployment info here">
            <span role="presentation" onClick={() => setIsOpen(true)}>
              <StyledImage src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/github-mark.svg" />
            </span>
          </Tooltip>
          <StyledModal isOpen={isOpen} onRequestClose={() => setIsOpen(false)} aside>
            <ModalHeader onCloseClicked={() => setIsOpen(false)}>
              Deployed {deployedHash} from{' '}
              {new Date(commitData[0].commit.committer.date).toLocaleDateString(
                navigator.language,
                dateFormatOptions
              )}
            </ModalHeader>
            <ModalBody
              style={{
                backgroundColor: '#F5F5F5',
                overflow: 'auto',
              }}
            >
              <TabContainer>
                <Tab
                  active={commitLogView === RELEASE_COMMIT_LOG_VIEW.INCLUDED}
                  onClick={() => setCommitLogView(RELEASE_COMMIT_LOG_VIEW.INCLUDED)}
                >
                  Latest deployed commits
                </Tab>

                <Tab
                  active={commitLogView === RELEASE_COMMIT_LOG_VIEW.NOT_INCLUDED}
                  onClick={() => setCommitLogView(RELEASE_COMMIT_LOG_VIEW.NOT_INCLUDED)}
                >
                  Non-deployed commits
                </Tab>
              </TabContainer>
              {commitLogView === RELEASE_COMMIT_LOG_VIEW.INCLUDED ? (
                <Box mt="1rem">
                  <Box mb="1rem">
                    <Text>
                      All commits included in this deployment can be viewed{' '}
                      <TextLink
                        target="_blank"
                        href={`https://github.com/merchantos/webPOS/commits/${deployedHash}`}
                      >
                        here
                      </TextLink>
                    </Text>
                    <Text mt=".5rem">Last 10 included commits are below</Text>
                    <Button
                      variant="secondary"
                      size="small"
                      mt=".5rem"
                      fill
                      onClick={() => refetchCommits()}
                      loading={isFetching || isLoading}
                    >
                      Refresh
                    </Button>
                  </Box>
                  {commitData.map((commit) => (
                    <CommitInfoRow key={commit.sha} {...commit} />
                  ))}
                </Box>
              ) : null}
              {commitLogView === RELEASE_COMMIT_LOG_VIEW.NOT_INCLUDED ? (
                <Box mt="1rem">
                  <Box>
                    <Text>
                      All commits <span style={{ ...BOLD_FONT }}>NOT</span> included in this
                      deployment can be viewed{' '}
                      <TextLink
                        target="_blank"
                        href={`https://github.com/merchantos/webPOS/compare/${deployedHash}...develop-retail`}
                      >
                        here
                      </TextLink>
                    </Text>
                  </Box>
                  <DiffList firstHash={deployedHash!} />
                </Box>
              ) : null}
            </ModalBody>
          </StyledModal>
        </>
      ) : null}
    </>
  );
};

const RenderedComponent = createReactRenderer({
  component: DeploymentInfoModal,
  selector: '[data-react-root="DeploymentInfoModal"]',
});

history.onChangeState(
  { form_name: 'ui_tab', tab: 'system', id: undefined },
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);

export default DeploymentInfoModal;
